import { createFileRoute, Link } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import { LegalLayout } from "@/components/legal/legal-layout";
import { SELLER } from "@/content/legal";
import { breadcrumbJsonLd, SITE_URL, BRAND_OG_IMAGE, BRAND_OG_IMAGE_ALT, SITE_NAME } from "@/lib/seo";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms & Conditions | Smart Word Labs" },
      {
        name: "description",
        content:
          "The terms governing use of Smart Word Labs's online English lessons for children aged 4-12, including payments, acceptable use and account termination.",
      },
      { property: "og:title", content: "Terms & Conditions | Smart Word Labs" },
      {
        property: "og:description",
        content: "Terms governing use of Smart Word Labs online English lessons.",
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: `${SITE_URL}/terms` },
      { property: "og:site_name", content: SITE_NAME },
      { property: "og:image", content: BRAND_OG_IMAGE },
      { property: "og:image:alt", content: BRAND_OG_IMAGE_ALT },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: BRAND_OG_IMAGE },
      { name: "twitter:image:alt", content: BRAND_OG_IMAGE_ALT },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/terms` }],
    scripts: [
      breadcrumbJsonLd([{ name: "Terms & Conditions", path: "/terms" }]),
    ],
  }),
  component: TermsPage,
});

function TermsPage() {
  const { t } = useTranslation("legal");
  const accountsItems = t("terms.sections.accounts.items", { returnObjects: true }) as string[];
  const acceptableUseItems = t("terms.sections.acceptableUse.items", {
    returnObjects: true,
  }) as string[];

  const tradingSuffix =
    SELLER.tradingName !== SELLER.legalName
      ? t("terms.tradingSuffix", { tradingName: SELLER.tradingName })
      : "";

  return (
    <LegalLayout title={t("terms.title")} updated={t("terms.updated")} path="/terms">
      <p>
        <Trans
          i18nKey="terms.intro"
          ns="legal"
          values={{
            tradingName: SELLER.tradingName,
            legalName: SELLER.legalName,
            tradingSuffix,
          }}
          components={{ strong: <strong /> }}
        />
      </p>

      <h2>{t("terms.sections.acceptance.heading")}</h2>
      <p>{t("terms.sections.acceptance.body")}</p>

      <h2>{t("terms.sections.service.heading")}</h2>
      <p>{t("terms.sections.service.body")}</p>

      <h2>{t("terms.sections.accounts.heading")}</h2>
      <ul>
        {accountsItems.map((item, i) => (
          <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
        ))}
      </ul>

      <h2>{t("terms.sections.acceptableUse.heading")}</h2>
      <p>{t("terms.sections.acceptableUse.intro")}</p>
      <ul>
        {acceptableUseItems.map((item, i) => (
          <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
        ))}
      </ul>

      <h2>{t("terms.sections.licence.heading")}</h2>
      <p>{t("terms.sections.licence.body")}</p>

      <h2>{t("terms.sections.ip.heading")}</h2>
      <p>{t("terms.sections.ip.body")}</p>

      <h2>{t("terms.sections.serviceLevel.heading")}</h2>
      <p>{t("terms.sections.serviceLevel.body")}</p>

      <h2>{t("terms.sections.payments.heading")}</h2>
      <p>{t("terms.sections.payments.body1")}</p>
      <p>
        <Trans
          i18nKey="terms.sections.payments.body2"
          ns="legal"
          components={[<a href="/refund-policy" key="0" />]}
        />
      </p>

      <h2>{t("terms.sections.suspension.heading")}</h2>
      <p dangerouslySetInnerHTML={{ __html: t("terms.sections.suspension.body") }} />

      <h2>{t("terms.sections.liability.heading")}</h2>
      <p>{t("terms.sections.liability.body")}</p>

      <h2>{t("terms.sections.indemnity.heading")}</h2>
      <p>{t("terms.sections.indemnity.body")}</p>

      <h2>{t("terms.sections.changes.heading")}</h2>
      <p>{t("terms.sections.changes.body")}</p>

      <h2>{t("terms.sections.general.heading")}</h2>
      <p dangerouslySetInnerHTML={{ __html: t("terms.sections.general.body") }} />

      <h2>{t("terms.sections.contact.heading")}</h2>
      <p>
        <Trans
          i18nKey="terms.sections.contact.body"
          ns="legal"
          components={[<Link to="/contact" key="0" />]}
        />
      </p>
    </LegalLayout>
  );
}
